import type { ResponsiveValue } from '@shopify/restyle/dist/types';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { Text } from 'app/design/primitives/text';

import { useRoute } from 'app/hooks/navigation';
import { useOnboarding } from 'app/hooks/use-onboarding';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from 'app/shopify';

interface OnboardingLayoutProps {
  primary: Pick<ButtonProps, 'disabled' | 'loading' | 'onPress'>;
  activeColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  titleKey?: string;
  showProgress?: boolean;
}

export function OnboardingLayout({
  children,
  primary,
  activeColor,
  titleKey,
  showProgress = true,
}: PropsWithChildren<OnboardingLayoutProps>) {
  const route = useRoute();
  const { t } = useTranslation('onboarding');
  const { current, max } = useOnboarding();

  const imagePerScreen = {
    login: images.web.login,
    'contact-information': images.web.contact_information,
    credentials: images.web.credentials,
    'personal-information': images.web.personal_information,
    guidelines: images.web.guidelines,
    'choose-sharer-type': images.web.quick_choose,
    'thank-you': images.web.thank_you,
    'your-selection': images.web.your_selection,
  };
  function isBridgeRoute(name: string): name is keyof typeof imagePerScreen {
    return name in imagePerScreen;
  }

  let currentImage;
  if (isBridgeRoute(route.name)) {
    currentImage = imagePerScreen[route.name];
  }
  return (
    <Row flex={1} backgroundColor="surface.default">
      <Column flex={1} justifyContent="center">
        <Column maxWidth={520} style={{ marginHorizontal: 'auto' }}>
          {showProgress ? <ProgressIndicator activeColor={activeColor} index={current} length={max} /> : null}

          <Text
            fontWeight="600"
            marginBottom="spacing.general.x4"
            marginTop="spacing.general.x8"
            textAlign="center"
            variant="headerXSmall"
          >
            {t(titleKey ?? 'title', { ns: route.name })}
          </Text>

          {children}

          <Button variant="primary" {...primary} marginTop="spacing.general.x4">
            <Text color="surface.default" textAlign="center">
              {current === max - 1 ? t('confirm') : t('nextStep')}
            </Text>
          </Button>
        </Column>
      </Column>

      <Column flex={1}>
        <PCImage flex={1} cachePolicy="memory-disk" source={currentImage} />
      </Column>
    </Row>
  );
}
